/**
 * Thin fetch wrapper for the IMP Pumps Portal API.
 *
 * Always sends `credentials: 'include'` so the httpOnly auth cookies travel
 * with every request. On 401 we transparently try `/api/auth/refresh` once
 * and re-issue the original request — that hides the 15-minute access-token
 * rotation from the rest of the app.
 *
 * This is intentionally lightweight (no React Query, no axios) because the
 * portal needs few client-side caching tricks. We can layer them in later.
 */

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details?: string[];
};

const API_BASE = "/api";

/**
 * Spring hands us a double-submit CSRF token in a JS-readable cookie; every
 * state-changing request has to echo it back in this header.
 *
 * Two cookie names on purpose: production uses the `__Host-` prefix (which the
 * browser only accepts with Secure + Path=/ + no Domain, so a sibling subdomain
 * cannot shadow it), and plain-HTTP localhost — where `__Host-` is rejected —
 * falls back to the bare name.
 */
const CSRF_HEADER = "X-XSRF-TOKEN";
const CSRF_COOKIE_NAMES = ["__Host-XSRF-TOKEN", "XSRF-TOKEN"];

function readCsrfToken(): string | null {
  for (const name of CSRF_COOKIE_NAMES) {
    const hit = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (hit) return decodeURIComponent(hit.slice(name.length + 1));
  }
  return null;
}

/**
 * Headers for a state-changing request made OUTSIDE `apiFetch` — the multipart
 * uploads and the SSE chat stream, which need their own fetch call. GETs don't
 * need it (Spring only enforces CSRF on mutating methods).
 */
export function csrfHeaders(): Record<string, string> {
  const token = readCsrfToken();
  return token ? { [CSRF_HEADER]: token } : {};
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Makes the server issue a CSRF cookie when the browser has none.
 *
 * <p>Every response carries one (SecurityConfig touches the deferred token on each request), so
 * the cheapest GET is enough and its result is deliberately ignored — including its status. A
 * 401 sets the cookie just as well as a 200.
 */
let csrfWarmInFlight: Promise<void> | null = null;

/**
 * Make sure the CSRF cookie exists before a mutating request that does NOT go through apiFetch.
 * apiFetch does this internally; any caller with its own fetch (the SSE chat) must do it too, or
 * a fresh session's first POST goes out headerless and dies as an unlogged 403 — the exact bug
 * fixed for apiFetch after the markets screen, resurfacing wherever the fix was not inherited.
 */
export async function ensureCsrfToken(): Promise<void> {
  if (!readCsrfToken()) await warmCsrfToken();
}

/**
 * A multipart POST, with the CSRF cookie guaranteed to exist first.
 *
 * <p>Seven upload paths had this same body copied out — FormData, a raw fetch, `csrfHeaders()`,
 * the same error mapping — and exactly one of them remembered to warm the token. The other six
 * could send a headerless POST on a fresh session and die as an unlogged 403; the owner hit it
 * uploading a category picture as SUPERADMIN, where the permission was never in question
 * (2026-10-05). The note on ensureCsrfToken predicted it: "resurfacing wherever the fix was not
 * inherited."
 *
 * <p>So the warm-up lives where the request is made, not in each caller's memory. These cannot go
 * through apiFetch: it sets Content-Type: application/json, and a multipart body must be left to
 * the browser so it can add the boundary.
 */
export async function postMultipart<T>(path: string, fd: FormData): Promise<T> {
  await ensureCsrfToken();
  const res = await send(path, fd);

  // No retry on 403. A first version re-warmed the token and sent again, which sounds prudent and
  // is not: the repository is CookieCsrfTokenRepository — plain double-submit, where the cookie IS
  // the token and the server only compares it against the header. There is no server-side token to
  // fall out of date with, so "stale" is really "cookie missing", which ensureCsrfToken above
  // already fixes. The retry could therefore almost never succeed, and its cost was re-uploading
  // the whole file. Raised by review of the change that added it.
  if (!res.ok) {
    let body: { code?: string; message?: string } = {};
    try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* no body */ }
    throw { status: res.status, code: body.code ?? `HTTP_${res.status}`, message: body.message ?? res.statusText };
  }
  return (await res.json()) as T;
}

function send(path: string, fd: FormData): Promise<Response> {
  return fetch(path, { method: "POST", credentials: "include", headers: csrfHeaders(), body: fd });
}

async function warmCsrfToken(): Promise<void> {
  if (!csrfWarmInFlight) {
    csrfWarmInFlight = fetch(`${API_BASE}/auth/me`, { credentials: "include" })
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        csrfWarmInFlight = null;
      });
  }
  await csrfWarmInFlight;
}

/**
 * Called once when a request 401s and the token refresh also fails — i.e. the
 * session is truly gone (refresh token expired/revoked). AuthContext registers
 * a handler that flips the app to anonymous so route guards redirect to /login
 * instead of widgets polling 401s against a dead session forever.
 */
let sessionExpiredHandler: (() => void) | null = null;
let sessionExpiredNotified = false;

export function setSessionExpiredHandler(fn: (() => void) | null) {
  sessionExpiredHandler = fn;
  sessionExpiredNotified = false;
}

function notifySessionExpired() {
  if (sessionExpiredNotified) return; // one burst of concurrent 401s → one notification
  sessionExpiredNotified = true;
  sessionExpiredHandler?.();
}

/** Re-arm the expiry notification after a successful login/refresh. */
export function resetSessionExpiredNotice() {
  sessionExpiredNotified = false;
}

async function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders(),
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        // Reset on next tick so callers piggybacking on the same in-flight refresh
        // all observe the same outcome.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });
  }
  return refreshInFlight;
}

async function readError(res: Response): Promise<ApiError> {
  let body: Partial<ApiError> = {};
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    /* ignore — non-JSON error */
  }
  return {
    status: res.status,
    code: body.code ?? `HTTP_${res.status}`,
    message: body.message ?? res.statusText,
    details: body.details,
  };
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** When true, do not attempt token refresh on 401 (use for /auth/login itself). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
};

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const body = opts.body !== undefined ? JSON.stringify(opts.body) : undefined;

  // A mutating call with no CSRF cookie is a guaranteed 403 — Spring checks CSRF BEFORE
  // authentication, so it comes back as {"code":"FORBIDDEN","message":"Access denied"} with
  // nothing in the server log to say why. Any response issues the cookie, so one cheap GET
  // turns that dead end into a working request.
  if (method !== "GET" && !readCsrfToken()) {
    await warmCsrfToken();
  }

  // Built PER ATTEMPT, not once. The retries below re-send this request after a token refresh,
  // and a single `init` captured the CSRF header as it was at the first attempt: if the cookie
  // was missing then, the header was absent, and every retry repeated the same header-less
  // request. One moment without a cookie locked that action out until the page was reloaded.
  const buildInit = (): RequestInit => ({
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(method === "GET" ? {} : csrfHeaders()),
    },
    body,
    signal: opts.signal,
  });

  const doFetch = () => fetch(`${API_BASE}${path}`, buildInit());
  let res = await doFetch();

  if (res.status === 401 && !opts.skipRefresh) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await doFetch();
    } else {
      notifySessionExpired();
    }
  }

  // A token still carrying totp_pending (e.g. 2FA enrolment just completed in another tab) is gated
  // server-side to the setup endpoints and 403s business calls. One refresh mints a clean, non-pending
  // token; retry once, then surface the original error.
  if (res.status === 403 && !opts.skipRefresh) {
    const err = await readError(res);
    if (err.code === "TOTP_SETUP_REQUIRED" && (await attemptRefresh())) {
      res = await doFetch();
    } else {
      throw err;
    }
  }

  if (!res.ok) {
    throw await readError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
